import { useEffect, useState } from 'preact/hooks'
import type { DesignSystemImportLogoDto } from '@/types/knowledgeBase'
import { readZipImages } from './designSystemZip'

/** Each logo found, as an address an `<img>` can show, once the zip has been read; an image that could not be read has none. */
export type ZipImages = { kind: 'reading' } | { kind: 'read'; urls: Record<string, string> }

/**
 * The logos a design-system zip carries, read from the person's own file for the import preview (ORB-1028). The addresses
 * are this page's alone and are let go when the preview closes.
 */
export function useZipImages(zip: File, logos: readonly DesignSystemImportLogoDto[]): ZipImages {
  const [images, setImages] = useState<ZipImages>({ kind: 'reading' })
  useEffect(() => {
    let live = true
    const made: string[] = []
    setImages({ kind: 'reading' })
    const done = (urls: Record<string, string>) => { if (live) setImages({ kind: 'read', urls }) }
    readZipImages(zip, logos.map(logo => logo.Path)).then(read => {
      // A preview closed while the zip was read makes no addresses, so there is none to let go of.
      if (!live) return
      const urls: Record<string, string> = {}
      for (const logo of logos) {
        const bytes = read.get(logo.Path)
        if (!bytes) continue
        const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: logo.ContentType }))
        made.push(url)
        urls[logo.Path] = url
      }
      done(urls)
    }, () => done({}))
    return () => {
      live = false
      made.forEach(url => URL.revokeObjectURL(url))
    }
  }, [zip, logos])
  return images
}
